import {
  AlertTriangle, Check, Eye, FileText, FileType2, GitCompare, Loader2,
  MessageSquarePlus, Send, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DOCUMENT_TYPE_LABELS } from "./Exports.constants";

function DiffLineBlock({
  side, lines, commentsBySide, commentDraft, setCommentDraft,
  handleCommentTyping, submitComment, toggleResolved, deleteComment,
  insertMention, mentionAnchor, setMentionAnchor, mentionCandidates, commentBusy,
}) {
  return (
    <div className="rounded border border-slate-200 bg-white p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{side === "left" ? "SINISTRA" : "DESTRA"}</p>
      <div className="space-y-0.5 font-mono text-[11px] leading-5" data-testid={`diff-${side}-pane`}>
        {lines.map((line, lineIdx) => {
          const comments = commentsBySide[side][lineIdx] || [];
          const bothComments = commentsBySide.both[lineIdx] || [];
          return (
            <div key={lineIdx} className="group">
              <div className="flex items-start gap-2 rounded px-1 hover:bg-slate-50">
                <span className="select-none w-8 shrink-0 text-right text-slate-400">{lineIdx + 1}</span>
                <span className="flex-1 whitespace-pre-wrap break-words">{line || "\u00A0"}</span>
                <button type="button" onClick={() => {
                  const k = `${side}:${lineIdx}`;
                  setCommentDraft({ ...commentDraft, [k]: commentDraft[k] !== undefined ? commentDraft[k] : "" });
                }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-900" aria-label="Aggiungi commento" data-testid={`add-comment-${side}-${lineIdx}`}>
                  <MessageSquarePlus className="h-3.5 w-3.5" />
                </button>
              </div>
              {[...comments, ...bothComments].map((c) => (
                <div key={c.id} className={`ml-10 mt-1 rounded border-l-4 ${c.resolved ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"} p-2 text-[11px]`} data-testid={`comment-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-800">{c.author_name} <span className="font-normal text-slate-500">({c.author_role})</span></span>
                    <span className="flex items-center gap-1">
                      <button type="button" onClick={() => toggleResolved(c)} className="text-emerald-700 hover:text-emerald-900" title="Toggle risolto" data-testid={`toggle-comment-${c.id}`}><Check className="h-3 w-3" /></button>
                      <button type="button" onClick={() => deleteComment(c)} className="text-red-600 hover:text-red-800" title="Elimina" data-testid={`delete-comment-${c.id}`}><X className="h-3 w-3" /></button>
                    </span>
                  </div>
                  <p className="mt-0.5 whitespace-pre-wrap text-slate-700">{c.text}</p>
                  {c.resolved && <Badge className="mt-1 bg-emerald-100 text-emerald-700">Risolto</Badge>}
                </div>
              ))}
              {commentDraft[`${side}:${lineIdx}`] !== undefined && (
                <div className="ml-10 mt-1" data-testid={`comment-draft-${side}-${lineIdx}`}>
                  <div className="flex items-center gap-1">
                    <Input
                      value={commentDraft[`${side}:${lineIdx}`]}
                      onChange={(e) => handleCommentTyping(`${side}:${lineIdx}`, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !mentionAnchor) { e.preventDefault(); submitComment(side, lineIdx); } }}
                      placeholder="Scrivi un commento... (usa @nome per menzionare)"
                      className="h-7 text-xs"
                      data-testid={`comment-input-${side}-${lineIdx}`}
                    />
                    <Button size="sm" onClick={() => submitComment(side, lineIdx)} disabled={commentBusy} className="h-7 bg-blue-900 hover:bg-blue-800" data-testid={`comment-submit-${side}-${lineIdx}`}>
                      <Send className="h-3 w-3" />
                    </Button>
                    <button type="button" onClick={() => { const c = { ...commentDraft }; delete c[`${side}:${lineIdx}`]; setCommentDraft(c); setMentionAnchor(null); }} className="text-slate-400 hover:text-slate-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {mentionAnchor && mentionAnchor.key === `${side}:${lineIdx}` && mentionCandidates.length > 0 && (
                    <div className="ml-0 mt-1 max-h-44 overflow-auto rounded border border-slate-200 bg-white shadow-md" data-testid={`mention-popover-${side}-${lineIdx}`}>
                      {mentionCandidates.map((c) => (
                        <button type="button" key={c.id} onClick={() => insertMention(c)} className="block w-full px-2 py-1 text-left text-xs hover:bg-blue-50" data-testid={`mention-item-${c.id}`}>
                          <span className="font-semibold text-slate-900">{c.name || c.email}</span>
                          <span className="ml-2 text-slate-500">@{c.mention_handle}</span>
                          <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">{c.role}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DiffDialog({
  diffOpen, setDiffOpen, documents,
  diffLeftId, setDiffLeftId, diffRightId, setDiffRightId,
  diffLeftText, setDiffLeftText, diffRightText, setDiffRightText,
  diffBusy, diffError, diffMode, setDiffMode,
  diffExportBusy, fetchDiffTexts, exportDiff,
  computedDiff, diffComments, canWrite,
  commentDraft, setCommentDraft, commentBusy,
  handleCommentTyping, submitComment, toggleResolved, deleteComment,
  insertMention, mentionAnchor, setMentionAnchor, mentionCandidates,
}) {
  const buildCommentsBySide = () => {
    const result = { left: {}, right: {}, both: {} };
    diffComments.forEach((c) => {
      const bucket = result[c.side];
      if (!bucket[c.line_index]) bucket[c.line_index] = [];
      bucket[c.line_index].push(c);
    });
    return result;
  };

  return (
    <Dialog open={diffOpen} onOpenChange={(open) => { if (!open) { setDiffOpen(false); setDiffLeftText(""); setDiffRightText(""); } }}>
      <DialogContent className="max-w-6xl h-[88vh] flex flex-col p-0 gap-0" data-testid="exports-diff-dialog">
        <DialogHeader className="border-b border-slate-200 p-4">
          <DialogTitle className="flex items-center gap-2"><GitCompare className="h-4 w-4" /> Confronto versioni</DialogTitle>
          <DialogDescription>
            Seleziona due documenti dello stesso tipo per visualizzare le differenze testuali (bozza vs finale/firmata).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Versione SINISTRA (es. bozza)</Label>
              <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={diffLeftId} onChange={(e) => setDiffLeftId(e.target.value)} data-testid="diff-left-select">
                <option value="">Seleziona...</option>
                {documents.map((d) => <option key={d.id} value={d.id}>{DOCUMENT_TYPE_LABELS[d.document_type]} v{d.version_number} · {d.status} · {d.generated_at?.slice(0, 16).replace("T", " ")}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Versione DESTRA (es. finale/firmata)</Label>
              <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={diffRightId} onChange={(e) => setDiffRightId(e.target.value)} data-testid="diff-right-select">
                <option value="">Seleziona...</option>
                {documents.map((d) => <option key={d.id} value={d.id}>{DOCUMENT_TYPE_LABELS[d.document_type]} v{d.version_number} · {d.status} · {d.generated_at?.slice(0, 16).replace("T", " ")}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={fetchDiffTexts} disabled={diffBusy || !diffLeftId || !diffRightId} className="bg-blue-900 hover:bg-blue-800" data-testid="diff-compute-button">
                {diffBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitCompare className="h-4 w-4" />}
                {diffBusy ? "Estrazione testo..." : "Confronta"}
              </Button>
              <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" role="group" data-testid="diff-mode-toggle">
                <button type="button" onClick={() => setDiffMode("lines")} className={`rounded px-3 py-1 text-xs ${diffMode === "lines" ? "bg-blue-900 text-white" : "text-slate-700"}`} data-testid="diff-mode-lines">Per riga</button>
                <button type="button" onClick={() => setDiffMode("words")} className={`rounded px-3 py-1 text-xs ${diffMode === "words" ? "bg-blue-900 text-white" : "text-slate-700"}`} data-testid="diff-mode-words">Per parola</button>
              </div>
              {canWrite && computedDiff && (
                <>
                  <Button variant="outline" size="sm" onClick={() => exportDiff("pdf")} disabled={diffExportBusy === "pdf"} data-testid="diff-export-pdf-button">
                    {diffExportBusy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    Esporta PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => exportDiff("docx")} disabled={diffExportBusy === "docx"} data-testid="diff-export-docx-button">
                    {diffExportBusy === "docx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileType2 className="h-4 w-4" />}
                    Esporta DOCX
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => exportDiff("pdf-visual")} disabled={diffExportBusy === "pdf-visual"} data-testid="diff-export-visual-button">
                    {diffExportBusy === "pdf-visual" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Diff visivo PDF
                  </Button>
                </>
              )}
            </div>
            {computedDiff && (
              <div className="flex items-center gap-3 text-xs" data-testid="diff-summary">
                <Badge className="bg-emerald-50 text-emerald-800"><span data-testid="diff-added-count">+{computedDiff.added}</span> {diffMode === "words" ? "tratti aggiunti" : "righe aggiunte"}</Badge>
                <Badge className="bg-red-50 text-red-800"><span data-testid="diff-removed-count">−{computedDiff.removed}</span> {diffMode === "words" ? "tratti rimossi" : "righe rimosse"}</Badge>
                <Badge className="bg-blue-50 text-blue-900" data-testid="diff-comments-count">{diffComments.length} commenti</Badge>
              </div>
            )}
          </div>
          {diffError && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{diffError}</AlertDescription></Alert>}
        </div>
        <div className="flex-1 overflow-auto border-t border-slate-200 bg-slate-50 p-2" data-testid="exports-diff-body">
          {!computedDiff && !diffBusy && (
            <p className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">Seleziona due documenti e clicca su <strong>Confronta</strong> per estrarre il testo e visualizzare le differenze.</p>
          )}
          {computedDiff && diffMode === "words" && (
            <div className="grid gap-2" data-testid="diff-panels-words">
              <div className="rounded border border-slate-200 bg-white p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Diff per parola (vista unificata)</p>
                <div className="whitespace-pre-wrap break-words font-mono text-[12px] leading-6" data-testid="diff-words-pane">
                  {computedDiff.parts.map((part, idx) => {
                    if (part.added) return <span key={idx} className="bg-emerald-200 text-emerald-900">{part.value}</span>;
                    if (part.removed) return <span key={idx} className="bg-red-200 text-red-900 line-through">{part.value}</span>;
                    return <span key={idx}>{part.value}</span>;
                  })}
                </div>
              </div>
            </div>
          )}
          {computedDiff && diffMode === "lines" && (() => {
            const leftLines = diffLeftText.split("\n");
            const rightLines = diffRightText.split("\n");
            const commentsBySide = buildCommentsBySide();
            const blockProps = {
              commentsBySide, commentDraft, setCommentDraft,
              handleCommentTyping, submitComment, toggleResolved, deleteComment,
              insertMention, mentionAnchor, setMentionAnchor, mentionCandidates, commentBusy,
            };
            return (
              <div className="grid gap-2 md:grid-cols-2" data-testid="diff-panels">
                <DiffLineBlock side="left" lines={leftLines} {...blockProps} />
                <DiffLineBlock side="right" lines={rightLines} {...blockProps} />
              </div>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
